import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';

export enum RightSidebarWidth {
  STANDARD = 275,
  WIDE = 550,
}

export interface RightSidebarConfirmationButton {
  text: string;
  onClick: () => void;
  disabled?: boolean;
  isPrimary?: boolean;
}

export interface RightSidebarSkeletonProps {
  title: string;
  children: React.ReactNode;
  confirmationButtons?: Array<RightSidebarConfirmationButton>;
  width: RightSidebarWidth;
}

/**
 * Basic UI outline for a right sidebar, with title, content, and confirmation buttons.
 */
export default function RightSidebarSkeleton({
  title,
  children,
  confirmationButtons,
  width,
}: RightSidebarSkeletonProps): JSX.Element {
  return (
    <div className={css(rightSidebarSkeletonStyles.container)} style={{width}}>
      <div className={css(rightSidebarSkeletonStyles.header)}>{title}</div>
      <div className={css(rightSidebarSkeletonStyles.body)}>{children}</div>
      {confirmationButtons && (
        <div className={css(rightSidebarSkeletonStyles.footer)} style={{width}}>
          {confirmationButtons.map(({text, disabled, onClick, isPrimary}, index) => (
            <Button
              buttonStyle={isPrimary ? ButtonStyle.SECONDARY : ButtonStyle.TERTIARY_GRAY}
              className={css(rightSidebarSkeletonStyles.confirmationButton)}
              disabled={disabled}
              key={`${title}-${index}`}
              onClick={onClick}
              size={ButtonSize.MEDIUM}
              text={text}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const DEFAULT_PADDING = 10;
export const STEP_CONFIG_PANEL_WIDTH = 500;

export const rightSidebarSkeletonStyles = StyleSheet.create({
  container: {
    background: Colors.WHITE,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between',
  },
  header: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    padding: DEFAULT_PADDING,
    borderBottom: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  body: {
    height: '100%',
    overflowY: 'auto',
    padding: DEFAULT_PADDING,
  },
  footer: {
    background: Colors.WHITE,
    borderTop: `1px solid ${Colors.GRAY_LIGHT}`,
    display: 'flex',
    padding: DEFAULT_PADDING,
  },
  confirmationButton: {
    flex: 1,
    margin: DEFAULT_PADDING / 2,
  },
});
